import "src/global.css";

import { useEffect } from "react";

import { usePathname } from "src/routes/hooks";

import { themeConfig, ThemeProvider } from "src/theme";
import "./App.css";
import { ProgressBar } from "src/components/progress-bar";
import { MotionLazy } from "src/components/animate/motion-lazy";
import {
  SettingsDrawer,
  defaultSettings,
  SettingsProvider,
} from "src/components/settings";

import { Provider } from "react-redux";
import { Toaster } from "sonner";
import { AuthProvider } from "./auth/context/jwt";
import { persistor, store } from "./store/store";
import { PersistGate } from "redux-persist/integration/react";

// ----------------------------------------------------------------------

type AppProps = {
  children: React.ReactNode;
};

export default function App({ children }: AppProps) {
  useScrollToTop();

  useEffect(() => {
    // Initialize Firebase notifications only
    const initNotifications = async () => {
      try {
        const { initializeNotifications } = await import("./firebase/firebase");
        await initializeNotifications();
      } catch (error) {
        // Silent fail
      }
    };

    initNotifications();

    const resizeHandler = (event: ErrorEvent) => {
      const resizeObserverError = /^ResizeObserver loop/;
      if (resizeObserverError.test(event.message)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    window.addEventListener("error", resizeHandler);

    return () => {
      window.removeEventListener("error", resizeHandler);
    };
  }, []);

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <AuthProvider>
          <SettingsProvider defaultSettings={defaultSettings}>
            <ThemeProvider
              noSsr
              defaultMode={themeConfig.defaultMode}
              modeStorageKey={themeConfig.modeStorageKey}
            >
              <MotionLazy>
                <Toaster position="top-right" richColors duration={2500} />
                <ProgressBar />
                <SettingsDrawer defaultSettings={defaultSettings} />
                {children}
              </MotionLazy>
            </ThemeProvider>
          </SettingsProvider>
        </AuthProvider>
      </PersistGate>
      <button
        onClick={async () => {
          const { requestPermission } = await import("./firebase/firebase");
          const success = await requestPermission();
          if (success) {
            console.log("Notification permission granted");
          } else {
            console.log("Notification permission denied");
          }
        }}
        style={{ position: "fixed", visibility: "hidden" }}
      >
        Enable Notification
      </button>
    </Provider>
  );
}

// ----------------------------------------------------------------------

function useScrollToTop() {
  const pathname = usePathname();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}
